import React, { useEffect, useMemo, useState, Component } from 'react';
import { X, Banknote, CreditCard, Smartphone, Loader2, Receipt, CheckSquare, Square, AlertCircle, CheckCircle2, Mail, Printer, ArrowLeft, FileText } from 'lucide-react';
import { Button } from '../ui/Button';
import { cn, formatCurrency } from '../../lib/utils';
import { useTableOrderItems } from '../../lib/table-hooks';
import { useBillingInfo, processPayment, openPrintableInvoice, PaymentItemSnapshot, PaymentRecord, BillingInfo } from '../../lib/billing-hooks';
import { TableWithOrder } from '../../lib/types';
interface PayTableModalProps {
  isOpen: boolean;
  onClose: () => void;
  table: TableWithOrder | null;
  restaurantId: number | string | undefined;
  onSuccess: (allPaid: boolean) => void;
  createdBy?: string | null;
}
type PayMethod = 'cash' | 'sinpe' | 'card';
type Step = 'select' | 'paid' | 'invoice';
export function PayTableModal({
  isOpen,
  onClose,
  table,
  restaurantId,
  onSuccess,
  createdBy
}: PayTableModalProps) {
  const orderId = table?.current_order?.id;
  const {
    items,
    loading: itemsLoading,
    refetch
  } = useTableOrderItems(table?.id, restaurantId ? String(restaurantId) : undefined);
  const {
    info: billingInfo,
    loading: infoLoading
  } = useBillingInfo(restaurantId);
  const [step, setStep] = useState<Step>('select');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [method, setMethod] = useState<PayMethod>('cash');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [completedPayment, setCompletedPayment] = useState<PaymentRecord | null>(null);
  const [allPaid, setAllPaid] = useState(false);
  // Customer billing data (only collected when generating invoice)
  const [customerName, setCustomerName] = useState('');
  const [customerLegalId, setCustomerLegalId] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const unpaidItems = useMemo(() => items.filter((i: any) => !i.paid_at), [items]);
  useEffect(() => {
    if (isOpen && unpaidItems.length > 0 && step === 'select') {
      setSelectedIds(new Set(unpaidItems.map(i => i.order_item_id)));
    }
    if (!isOpen) {
      setStep('select');
      setSelectedIds(new Set());
      setError(null);
      setMethod('cash');
      setCompletedPayment(null);
      setAllPaid(false);
      setCustomerName('');
      setCustomerLegalId('');
      setCustomerEmail('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, unpaidItems.length]);
  const toggleItem = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);else next.add(id);
      return next;
    });
  };
  const allSelected = unpaidItems.length > 0 && selectedIds.size === unpaidItems.length;
  const toggleAll = () => {
    if (allSelected) setSelectedIds(new Set());else setSelectedIds(new Set(unpaidItems.map(i => i.order_item_id)));
  };
  const selectedItems = unpaidItems.filter(i => selectedIds.has(i.order_item_id));
  const total = selectedItems.reduce((s, i) => s + (i.line_total || 0), 0);
  const isPartial = selectedItems.length < unpaidItems.length;
  const handleSubmit = async () => {
    if (!orderId || !restaurantId) {
      setError('Falta información de la mesa.');
      return;
    }
    if (selectedItems.length === 0) {
      setError('Selecciona al menos un ítem para cobrar.');
      return;
    }
    setError(null);
    setSubmitting(true);
    const snapshot: PaymentItemSnapshot[] = selectedItems.map(i => ({
      order_item_id: i.order_item_id,
      item_name: i.item_name,
      quantity: i.quantity,
      unit_price: i.unit_price,
      line_total: i.line_total,
      notes: i.notes || null
    }));
    const result = await processPayment({
      restaurantId,
      orderId,
      tableId: table?.id || null,
      amount: total,
      method,
      items: snapshot,
      isPartial,
      createdBy: createdBy || null
    });
    setSubmitting(false);
    if (!result.success || !result.payment) {
      setError(result.error || 'Error al procesar el pago.');
      return;
    }
    await refetch();
    const allPaidNow = !isPartial || selectedItems.length === unpaidItems.length;
    setCompletedPayment(result.payment);
    setAllPaid(allPaidNow);
    setStep('paid');
    onSuccess(allPaidNow);
  };
  if (!isOpen || !table) return null;
  return <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-id="element-817">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[92vh] flex flex-col shadow-2xl" data-id="element-818">
        {step === 'select' && <SelectStep table={table} billingInfo={billingInfo} infoLoading={infoLoading} itemsLoading={itemsLoading} unpaidItems={unpaidItems} selectedIds={selectedIds} allSelected={allSelected} toggleAll={toggleAll} toggleItem={toggleItem} method={method} setMethod={setMethod} error={error} isPartial={isPartial} total={total} selectedItems={selectedItems} submitting={submitting} onClose={onClose} onSubmit={handleSubmit} data-id="element-819" />}

        {step === 'paid' && completedPayment && <PaidStep payment={completedPayment} allPaid={allPaid} onClose={onClose} onGoInvoice={() => setStep('invoice')} onChargeRemaining={() => {
        setStep('select');
        setCompletedPayment(null);
        setSelectedIds(new Set());
      }} data-id="element-820" />}

        {step === 'invoice' && completedPayment && billingInfo && <InvoiceStep table={table} billingInfo={billingInfo} payment={completedPayment} customerName={customerName} setCustomerName={setCustomerName} customerLegalId={customerLegalId} setCustomerLegalId={setCustomerLegalId} customerEmail={customerEmail} setCustomerEmail={setCustomerEmail} onBack={() => setStep('paid')} onClose={onClose} data-id="element-821" />}
      </div>
    </div>;
}
// ─── STEP 1: Select items + payment method ────────────────────────────────
function SelectStep(props: {
  table: TableWithOrder;
  billingInfo: BillingInfo | null;
  infoLoading: boolean;
  itemsLoading: boolean;
  unpaidItems: any[];
  selectedIds: Set<string>;
  allSelected: boolean;
  toggleAll: () => void;
  toggleItem: (id: string) => void;
  method: 'cash' | 'sinpe' | 'card';
  setMethod: (m: 'cash' | 'sinpe' | 'card') => void;
  error: string | null;
  isPartial: boolean;
  total: number;
  selectedItems: any[];
  submitting: boolean;
  onClose: () => void;
  onSubmit: () => void;
}) {
  const {
    table,
    itemsLoading,
    unpaidItems,
    selectedIds,
    allSelected,
    toggleAll,
    toggleItem,
    method,
    setMethod,
    error,
    isPartial,
    total,
    selectedItems,
    submitting,
    onClose,
    onSubmit
  } = props;
  return <>
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0" data-id="element-822">
        <div data-id="element-823">
          <h2 className="text-xl font-black text-gray-900 flex items-center gap-2" data-id="element-824">
            <Receipt className="w-5 h-5 text-amber-500" data-id="element-825" />
            Cobrar {table.table_name || `Mesa ${table.table_number}`}
          </h2>
          <p className="text-xs text-gray-500 mt-0.5" data-id="element-826">
            Selecciona los ítems a cobrar (división de cuenta disponible)
          </p>
        </div>
        <button onClick={onClose} disabled={submitting} className="p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-40" data-id="element-827">
          <X className="w-5 h-5 text-gray-500" data-id="element-828" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4" data-id="element-829">
        {itemsLoading ? <div className="flex justify-center py-12" data-id="element-830">
            <Loader2 className="w-6 h-6 text-gray-400 animate-spin" data-id="element-831" />
          </div> : unpaidItems.length === 0 ? <div className="text-center py-12 text-gray-500" data-id="element-832">
            <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-400 mb-3" data-id="element-833" />
            <p className="font-bold" data-id="element-834">No hay ítems pendientes de cobro</p>
            <p className="text-sm" data-id="element-835">Esta mesa ya fue cobrada por completo.</p>
          </div> : <>
            <button onClick={toggleAll} className="w-full flex items-center justify-between px-4 py-2.5 bg-gray-50 hover:bg-gray-100 rounded-xl transition-colors" data-id="element-836">
              <span className="flex items-center gap-2 text-sm font-bold text-gray-900" data-id="element-837">
                {allSelected ? <CheckSquare className="w-4 h-4 text-amber-500" data-id="element-838" /> : <Square className="w-4 h-4 text-gray-400" data-id="element-839" />}
                {allSelected ? 'Deseleccionar todo' : 'Seleccionar todo'}
              </span>
              <span className="text-xs font-medium text-gray-500" data-id="element-840">
                {selectedIds.size} / {unpaidItems.length} ítems
              </span>
            </button>

            <div className="space-y-1.5" data-id="element-841">
              {unpaidItems.map(item => {
            const isSelected = selectedIds.has(item.order_item_id);
            return <button key={item.order_item_id} onClick={() => toggleItem(item.order_item_id)} className={cn('w-full text-left flex items-start gap-3 px-3 py-3 rounded-xl border-2 transition-all', isSelected ? 'border-amber-400 bg-amber-50/50' : 'border-gray-100 hover:border-gray-200 bg-white')} data-id="element-842">
                    <div className="mt-0.5 shrink-0" data-id="element-843">
                      {isSelected ? <CheckSquare className="w-5 h-5 text-amber-500" data-id="element-844" /> : <Square className="w-5 h-5 text-gray-300" data-id="element-845" />}
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-846">
                      <div className="flex items-baseline gap-2" data-id="element-847">
                        <span className="font-black text-gray-900 bg-gray-100 px-1.5 rounded text-xs" data-id="element-848">
                          {item.quantity}×
                        </span>
                        <p className="font-bold text-gray-900 text-sm truncate" data-id="element-849">
                          {item.item_name}
                        </p>
                      </div>
                      {item.notes && <p className="text-xs text-gray-500 italic mt-0.5" data-id="element-850">
                          {item.notes}
                        </p>}
                    </div>
                    <span className="font-bold text-gray-900 text-sm tabular-nums shrink-0" data-id="element-851">
                      {formatCurrency(item.line_total)}
                    </span>
                  </button>;
          })}
            </div>

            <div className="pt-2" data-id="element-852">
              <p className="text-xs font-bold text-gray-700 uppercase tracking-wider mb-2" data-id="element-853">
                Método de pago
              </p>
              <div className="grid grid-cols-3 gap-2" data-id="element-854">
                {([{
              id: 'cash',
              label: 'Efectivo',
              Icon: Banknote
            }, {
              id: 'sinpe',
              label: 'SINPE',
              Icon: Smartphone
            }, {
              id: 'card',
              label: 'Tarjeta',
              Icon: CreditCard
            }] as const).map(({
              id,
              label,
              Icon
            }) => <button key={id} onClick={() => setMethod(id)} className={cn('flex flex-col items-center justify-center p-3 rounded-xl border-2 transition-all', method === id ? 'border-amber-400 bg-amber-50 text-amber-700' : 'border-gray-100 hover:border-gray-200 text-gray-600')} data-id="element-855">
                    <Icon className="w-5 h-5 mb-1" data-id="element-856" />
                    <span className="text-xs font-bold" data-id="element-857">{label}</span>
                  </button>)}
              </div>
            </div>

            {error && <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700 flex items-start gap-2" data-id="element-858">
                <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" data-id="element-859" />
                {error}
              </div>}
          </>}
      </div>

      {unpaidItems.length > 0 && <div className="px-6 py-4 border-t border-gray-100 shrink-0 bg-gray-50 rounded-b-2xl" data-id="element-860">
          <div className="flex justify-between items-baseline mb-3" data-id="element-861">
            <div data-id="element-862">
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider" data-id="element-863">
                {isPartial ? 'Total parcial' : 'Total a cobrar'}
              </p>
              {isPartial && <p className="text-[10px] text-amber-600 font-bold" data-id="element-864">
                  Cuenta dividida — quedará saldo pendiente
                </p>}
            </div>
            <span className="text-3xl font-black text-amber-500 tabular-nums" data-id="element-865">
              {formatCurrency(total)}
            </span>
          </div>
          <Button fullWidth size="lg" onClick={onSubmit} disabled={submitting || selectedItems.length === 0} className="bg-gray-900 hover:bg-gray-800 text-white font-bold shadow-lg" data-id="element-866">
            {submitting ? <span className="flex items-center justify-center gap-2" data-id="element-867">
                <Loader2 className="w-5 h-5 animate-spin" data-id="element-868" />
                Procesando...
              </span> : <span className="flex items-center justify-center gap-2" data-id="element-869">
                <Banknote className="w-5 h-5" data-id="element-870" />
                Cobrar
              </span>}
          </Button>
        </div>}
    </>;
}
// ─── STEP 2: Payment confirmed — choose: invoice or close ─────────────────
function PaidStep(props: {
  payment: PaymentRecord;
  allPaid: boolean;
  onClose: () => void;
  onGoInvoice: () => void;
  onChargeRemaining: () => void;
}) {
  const {
    payment,
    allPaid,
    onClose,
    onGoInvoice,
    onChargeRemaining
  } = props;
  const methodLabel = payment.method === 'cash' ? 'Efectivo' : payment.method === 'sinpe' ? 'SINPE Móvil' : 'Tarjeta';
  return <>
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0" data-id="element-871">
        <div className="flex items-center gap-3" data-id="element-872">
          <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center" data-id="element-873">
            <CheckCircle2 className="w-6 h-6 text-emerald-500" data-id="element-874" />
          </div>
          <div data-id="element-875">
            <h2 className="text-xl font-black text-gray-900" data-id="element-876">
              Pago registrado
            </h2>
            <p className="text-xs text-gray-500" data-id="element-877">
              {methodLabel} · {formatCurrency(payment.amount)}
              {!allPaid && ' · Saldo pendiente en mesa'}
            </p>
          </div>
        </div>
        <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-878">
          <X className="w-5 h-5 text-gray-500" data-id="element-879" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-8" data-id="element-880">
        <div className="text-center mb-6" data-id="element-881">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-emerald-50 mb-4" data-id="element-882">
            <CheckCircle2 className="w-12 h-12 text-emerald-500" data-id="element-883" />
          </div>
          <h3 className="text-2xl font-black text-gray-900 mb-1" data-id="element-884">
            ¡Cobro exitoso!
          </h3>
          <p className="text-sm text-gray-500" data-id="element-885">¿El cliente desea factura?</p>
        </div>

        <div className="max-w-sm mx-auto space-y-2" data-id="element-886">
          <button onClick={onGoInvoice} className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl bg-gray-900 hover:bg-gray-800 text-white font-bold transition-colors" data-id="element-887">
            <FileText className="w-5 h-5" data-id="element-888" />
            Sí, generar factura
          </button>
          {!allPaid ? <button onClick={onChargeRemaining} className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-amber-500 hover:bg-amber-600 text-white font-bold transition-colors" data-id="element-889">
              <ArrowLeft className="w-4 h-4" data-id="element-890" />
              Cobrar saldo restante
            </button> : <button onClick={onClose} className="w-full py-3 rounded-xl border-2 border-gray-200 hover:border-gray-300 text-gray-700 font-bold transition-colors" data-id="element-891">
              No, cerrar
            </button>}
        </div>
      </div>
    </>;
}
// ─── STEP 3: Generate invoice — collect customer data + send by email ────
function InvoiceStep(props: {
  table: TableWithOrder;
  billingInfo: BillingInfo;
  payment: PaymentRecord;
  customerName: string;
  setCustomerName: (s: string) => void;
  customerLegalId: string;
  setCustomerLegalId: (s: string) => void;
  customerEmail: string;
  setCustomerEmail: (s: string) => void;
  onBack: () => void;
  onClose: () => void;
}) {
  const {
    table,
    billingInfo,
    payment,
    customerName,
    setCustomerName,
    customerLegalId,
    setCustomerLegalId,
    customerEmail,
    setCustomerEmail,
    onBack,
    onClose
  } = props;
  const consecStr = String(payment.consecutive_number).padStart(6, '0');
  const invoiceUrl = `${window.location.origin}/factura/${payment.id}`;
  const buildEmailBody = () => {
    const greeting = customerName.trim() || 'Cliente';
    return `Estimad@ ${greeting},\n\n` + `Agradecemos profundamente su visita. Esperamos que su paso por nuestras instalaciones haya sido de su agrado.\n\n` + (customerLegalId.trim() ? `Datos del cliente:\n` + `Nombre: ${greeting}\n` + `Cédula / Identificación: ${customerLegalId.trim()}\n\n` : '') + `Adjunto podrá encontrar la factura solicitada:\n` + `${invoiceUrl}\n\n` + `Factura N° ${consecStr} · Total: ${new Intl.NumberFormat('es-CR', {
      style: 'currency',
      currency: 'CRC',
      minimumFractionDigits: 0
    }).format(payment.amount)}\n\n` + `¡Lo esperamos pronto!\n\n` + `Att: ${billingInfo.business_name}`;
  };
  const handleSendEmail = () => {
    if (!customerEmail.trim()) {
      alert('Ingresa el correo electrónico del cliente.');
      return;
    }
    const subject = `Factura ${consecStr} — ${billingInfo.business_name}`;
    const url = `mailto:${encodeURIComponent(customerEmail)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(buildEmailBody())}`;
    window.location.href = url;
  };
  const handlePrint = () => {
    openPrintableInvoice({
      info: billingInfo,
      payment,
      tableLabel: table.table_name || `Mesa ${table.table_number}`
    });
  };
  const canSend = customerName.trim() && customerEmail.trim();
  return <>
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0" data-id="element-892">
        <div className="flex items-center gap-3" data-id="element-893">
          <button onClick={onBack} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-894">
            <ArrowLeft className="w-4 h-4 text-gray-600" data-id="element-895" />
          </button>
          <div data-id="element-896">
            <h2 className="text-xl font-black text-gray-900 flex items-center gap-2" data-id="element-897">
              <FileText className="w-5 h-5 text-gray-700" data-id="element-898" />
              Generar factura
            </h2>
            <p className="text-xs text-gray-500" data-id="element-899">
              Factura N° {consecStr} · {formatCurrency(payment.amount)}
            </p>
          </div>
        </div>
        <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-900">
          <X className="w-5 h-5 text-gray-500" data-id="element-901" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4" data-id="element-902">
        <p className="text-xs text-gray-500 leading-relaxed bg-gray-50 border border-gray-100 rounded-xl px-4 py-3" data-id="element-903">
          Ingresa los datos del cliente para emitir la factura. Se enviará por
          correo electrónico con un enlace al comprobante.
        </p>

        <div data-id="element-904">
          <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5" data-id="element-905">
            Nombre completo *
          </label>
          <input type="text" value={customerName} onChange={e => setCustomerName(e.target.value)} placeholder="Ej. María Pérez Rojas" className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-100 focus:border-amber-400 focus:outline-none text-sm font-medium text-gray-900 placeholder:text-gray-400 bg-white" data-id="element-906" />
        </div>

        <div data-id="element-907">
          <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5" data-id="element-908">
            Cédula / Identificación
            <span className="text-gray-400 font-medium normal-case ml-1" data-id="element-909">
              (opcional)
            </span>
          </label>
          <input type="text" value={customerLegalId} onChange={e => setCustomerLegalId(e.target.value)} placeholder="Ej. 1-1234-5678" className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-100 focus:border-amber-400 focus:outline-none text-sm font-medium text-gray-900 placeholder:text-gray-400 bg-white" data-id="element-910" />
        </div>

        <div data-id="element-911">
          <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5" data-id="element-912">
            Correo electrónico *
          </label>
          <input type="email" value={customerEmail} onChange={e => setCustomerEmail(e.target.value)} placeholder="cliente@correo.com" className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-100 focus:border-amber-400 focus:outline-none text-sm font-medium text-gray-900 placeholder:text-gray-400 bg-white" data-id="element-913" />
        </div>
      </div>

      <div className="px-6 py-4 border-t border-gray-100 shrink-0 bg-gray-50 rounded-b-2xl flex flex-col sm:flex-row gap-2" data-id="element-914">
        <Button variant="outline" onClick={handlePrint} className="flex-1 flex items-center justify-center gap-2 font-bold" data-id="element-915">
          <Printer className="w-4 h-4" data-id="element-916" />
          Imprimir
        </Button>
        <Button onClick={handleSendEmail} disabled={!canSend} className="flex-1 bg-gray-900 hover:bg-gray-800 text-white font-bold flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-917">
          <Mail className="w-4 h-4" data-id="element-918" />
          Enviar por correo
        </Button>
      </div>
    </>;
}